/**
 * Excel bulk-upload column order — keep in sync with backend/app/Config/ExcelTemplateConfig.php
 * (CUSTOMER_COLUMN_ORDER / VENDOR_COLUMN_ORDER).
 */

/** GST: treatment → GSTIN → PAN; location: country → state → city → postal_code */
export const CUSTOMER_EXCEL_TEMPLATE_COLUMNS = [
  "customer_name",
  "gst_treatment",
  "gstin_vat_tax_id",
  "pan_tax_registration_no",
  "country",
  "state",
  "city",
  "postal_code",
  "payment_terms",
  "currency",
  "contact_name_1",
  "contact_number_1",
  "contact_email_1",
  "contact_name_2",
  "contact_number_2",
  "contact_email_2",
  "billing_address",
  "shipping_address",
  "credit_limit",
] as const;

export const VENDOR_EXCEL_TEMPLATE_COLUMNS = [
  "vendor_legal_name",
  "vendor_legal_entity",
  "type_of_vendor",
  "gst_treatment",
  "gst_category",
  "gstin",
  "pan_tax_id",
  "country",
  "state",
  "city",
  "postal_code",
  "payment_terms",
  "currency",
  "msme_flag",
  "udyam_number",
  "tds_applicable",
  "tds_tax_rate",
  "business_category",
  "trading_name",
  "registered_office_address",
  "primary_contact_name",
  "primary_contact_phone",
  "primary_contact_email",
  "beneficiary_name",
  "bank_account_number",
  "bank_name",
  "branch",
  "ifsc_swift_code",
] as const;

export const GL_EXCEL_TEMPLATE_COLUMNS = [
  "account_type",
  "account_name",
  "is_sub_account",
  "parent_gl_name",
  "description",
  "use_in_invoices",
  "use_in_bills",
] as const;

/** Tally GL bulk template — keep in sync with ExcelTemplateConfig::GL_TALLY_COLUMN_ORDER. */
export const GL_TALLY_EXCEL_TEMPLATE_COLUMNS = [
  "node_kind",
  "account_type",
  "account_name",
  "parent_gl_name",
  "description",
  "use_in_invoices",
  "use_in_bills",
] as const;

/** Internal payload keys — not shown in GL bulk review row tables. */
const GL_BULK_REVIEW_HIDDEN_COLUMNS = new Set(["invoice", "bill"]);

export type CustomerExcelTemplateColumn =
  (typeof CUSTOMER_EXCEL_TEMPLATE_COLUMNS)[number];

export type VendorExcelTemplateColumn =
  (typeof VENDOR_EXCEL_TEMPLATE_COLUMNS)[number];

export type GlExcelTemplateColumn =
  (typeof GL_EXCEL_TEMPLATE_COLUMNS)[number];

/** Column field names for bulk-upload review UI, in Excel template order. */
export function getExcelTemplateColumnOrder(
  entityType: "customer" | "vendor" | "gl",
  options?: { backend?: "zoho" | "tally" | string },
): readonly string[] {
  if (entityType === "customer") {
    return CUSTOMER_EXCEL_TEMPLATE_COLUMNS;
  }
  if (entityType === "gl") {
    return options?.backend === "tally"
      ? GL_TALLY_EXCEL_TEMPLATE_COLUMNS
      : GL_EXCEL_TEMPLATE_COLUMNS;
  }
  return VENDOR_EXCEL_TEMPLATE_COLUMNS;
}

/**
 * Template columns first (full set), then any extra keys present in row payloads.
 */
export function resolveBulkUploadReviewColumns(
  entityType: "customer" | "vendor" | "gl",
  rows: Array<{ payload?: Record<string, unknown> }>,
  options?: { backend?: "zoho" | "tally" | string },
): string[] {
  const inferredBackend =
    options?.backend ??
    (entityType === "gl" &&
    rows.some((r) => r.payload && "node_kind" in (r.payload ?? {}))
      ? "tally"
      : undefined);
  const templateOrder = getExcelTemplateColumnOrder(entityType, {
    backend: inferredBackend,
  });
  const templateColumns = new Set<string>(templateOrder);
  const extra = new Set<string>();

  for (const row of rows) {
    Object.keys(row.payload ?? {}).forEach((key) => {
      if (templateColumns.has(key)) {
        return;
      }
      if (entityType === "gl" && GL_BULK_REVIEW_HIDDEN_COLUMNS.has(key)) {
        return;
      }
      // Hide internal place-under aliases on Tally reviews (parent_gl_name gates nesting).
      if (
        entityType === "gl" &&
        inferredBackend === "tally" &&
        (key === "is_sub_account" || key === "place_under_group")
      ) {
        return;
      }
      extra.add(key);
    });
  }

  const extrasSorted = Array.from(extra).sort((a, b) => a.localeCompare(b));
  return [...templateOrder, ...extrasSorted];
}
